import { forwardRef, memo, useImperativeHandle, useMemo, useState } from 'react';
import _ from 'lodash';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { t } from '@lingui/macro';
import { Box, CircularProgress, MenuItem, Typography } from '@material-ui/core';
import { useDataService } from '../../../services/ApiDataService';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { useFind } from '../../../services/FormQueryService';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';

import FormSection from '../../form/FormSection';
import NgpForm from '../../form/NgpForm';
import NgpSelect from '../../form/NgpSelect';
import NgpNativeNumberField from '../../form/NgpNativeNumberField';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import {
    dirtyFormBodyText,
    dirtyFormCancelText,
    dirtyFormConfirmText,
    dirtyFormTitleText,
    useStyles as useDirtyPromptStyles,
} from '../../form/DirtyFormConfirm';
import {
    INFRA_THRESHOLD_URL,
    createMissingInfrastructureThresholds,
    getCbQosClasses,
    updateInfrastructureThreshold,
    updateCbQosClass,
} from './ThresholdsApi';
import { handleThresholdSaveError, mapThresholdValidationField } from './ThresholdsSaveError';

import {
    GROUP_TYPE_FIELD,
    INFRASTRUCTURE_CBQOS,
    CBQOS_OPTIONS,
    CBQOS_OPTION_ENABLED_STATUS,
    CBQOS_OPTION_DISABLED,
} from './ThresholdsConstants';
import { useStyles } from './ThresholdsInfrastructureTabStyles';
import { RowFlexLayout } from '../../layout';

const INFRA_GROUPS = ['Server', 'Interface', 'Wireless', 'Network Device', 'VMware', 'CBQoS'];

const INFRA_METRIC_NAMES = {
    Server: ['CPUUtilization', 'diskConsumption'],
    Interface: ['errorRate', 'discardRate', 'utilization'],
    Wireless: ['retryRate', 'receivedFrameErrorRate', 'channel24Utilization', 'channel5Utilization'],
    VMware: ['CPUUtilization', 'memoryUtilization', 'maxDiskLatency'],
    'Network Device': ['CPUUtilization', 'memoryUtilization'],
};

const INFRA_METRIC_LABELS = {
    CPUUtilization: t`CPU Utilization`,
    memoryUtilization: t`Memory Utilization`,
    maxDiskLatency: t`Max Disk Latency`,
    diskConsumption: t`Disk Consumption`,
    errorRate: t`Errors`,
    discardRate: t`Total Discards`,
    retryRate: t`Retry Rate`,
    receivedFrameErrorRate: t`Received Frame Error Rate`,
    channel24Utilization: t`2.4Ghz Channel Utilization`,
    channel5Utilization: t`5Ghz Channel Utilization`,
    utilization: t`Utilization`,
};

function metricTitleFallback(metricName) {
    if (!metricName) {
        return '';
    }

    // Convert camelCase or mixed metric keys into a readable title.
    return metricName
        .replace(/([a-z\d])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .trim();
}

function findCbQosOption(monitor, status) {
    const option = _.find(CBQOS_OPTIONS, (opt) => opt.monitor === monitor && opt.status === status);
    return option ? option.value : '';
}

function getCbQosMetricId(metricIds, cbQosClasses) {
    let metricId = _.first(metricIds);

    if (!_.find(cbQosClasses, { metricId }) && metricIds.length > 1) {
        const [, alternateMetricId] = metricIds;
        metricId = alternateMetricId;
    }

    return metricId;
}

function buildInfraFormDefaults(thresholds, cbQosClasses) {
    const byId = {};
    thresholds.forEach((item) => {
        let metricId = _.first(item.metricIds);
        const inputInteger = item.units === 'count per polling interval';
        const isCbQos = item.group === INFRASTRUCTURE_CBQOS;

        // CBQoS thresholds can have a metric ID for both directions;
        // the one we want may not be the first one in the array.
        if (isCbQos) {
            metricId = getCbQosMetricId(item.metricIds, cbQosClasses);
        }

        const entry = {
            degraded: item.degraded,
            critical: item.excessive,
            metricId,
            enableDegraded: item.enableDegraded !== false,
            enableExcessive: item.enableExcessive !== false,
            inputInteger,
        };

        if (isCbQos) {
            const cbQosClass = _.find(cbQosClasses, { metricId });
            entry.cbQosOption = cbQosClass
                ? findCbQosOption(cbQosClass.enableMonitoring, cbQosClass.enableStatus)
                : CBQOS_OPTION_DISABLED;
            entry.cbQosMetricIds = item.metricIds;
        }

        byId[item._id] = entry;
    });

    return { byId };
}

function metricLabelForInfrastructure(group, threshold) {
    const metricId = _.first(threshold.metricIds);

    if (group === 'CBQoS') {
        return t`Metric ${metricId}`;
    }

    const metricName = INFRA_METRIC_NAMES[group]?.[metricId];
    const metricTitle = threshold?.metric?.title || threshold?.metricTitle || metricTitleFallback(metricName);
    return INFRA_METRIC_LABELS[metricName] || metricTitle || t`Metric ${metricId}`;
}

function getCbQosTitle(cbQosClasses, metricId) {
    const cbQosClass = _.find(cbQosClasses, { metricId });
    return cbQosClass ? cbQosClass.name : t`(Unknown)`;
}

function isCbQosFieldDisabled(cbQosOption) {
    return cbQosOption !== CBQOS_OPTION_ENABLED_STATUS;
}

function CbQosThresholdRow({ threshold, metricLabel, classes }) {
    const thresholdId = threshold._id;
    const cbQosOption = useWatch({ name: `byId.${thresholdId}.cbQosOption` });
    const fieldsDisabled = isCbQosFieldDisabled(cbQosOption);

    // These values come from server data and never change via user input,
    // so derive them from the threshold prop instead of useWatch to avoid
    // excessive re-renders during form reset().
    const showDegraded = threshold.enableDegraded !== false;
    const showExcessive = threshold.enableExcessive !== false;
    const stepValue = threshold.units === 'count per polling interval' ? 1 : 'any';

    return (
        <FormSection key={thresholdId}>
            <RowFlexLayout vBottom ml={7}>
                <Typography variant="subtitle2" className={classes.cbqosMetricLabel} title={metricLabel}>
                    {metricLabel}
                </Typography>
                {showDegraded && (
                    <Box ml={4}>
                        <NgpNativeNumberField
                            required={!fieldsDisabled}
                            fieldName={`byId.${thresholdId}.degraded`}
                            label={t`Degraded`}
                            min={threshold.min}
                            max={threshold.max}
                            step={stepValue}
                            disabled={fieldsDisabled}
                            fullWidth
                        />
                    </Box>
                )}
                {showExcessive && (
                    <Box ml={4}>
                        <NgpNativeNumberField
                            required={!fieldsDisabled}
                            fieldName={`byId.${thresholdId}.critical`}
                            label={t`Excessive`}
                            min={threshold.min}
                            max={threshold.max}
                            step={stepValue}
                            disabled={fieldsDisabled}
                            fullWidth
                        />
                    </Box>
                )}
                <Box ml={4}>
                    <NgpSelect
                        fieldName={`byId.${thresholdId}.cbQosOption`}
                        label={t`Option`}
                        className={classes.cbqosOptionSelect}
                    >
                        {CBQOS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </NgpSelect>
                </Box>
            </RowFlexLayout>
        </FormSection>
    );
}

const MemoizedCbQosThresholdRow = memo(CbQosThresholdRow);

function normalizeThresholdsResponse(response) {
    if (Array.isArray(response)) {
        return response;
    }

    if (Array.isArray(response?.data)) {
        return response.data;
    }

    return [];
}

const defaultFormValues = {
    [GROUP_TYPE_FIELD]: 'Server',
    byId: {},
};

const ThresholdsInfrastructureTab = forwardRef((props, ref) => {
    const classes = useStyles();
    const dirtyPromptClasses = useDirtyPromptStyles();
    const dataService = useDataService();
    const { errorWrapper } = useErrorWrapper();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const formSaveService = useFormSaveService();

    const [infraGroup, setInfraGroup] = useState('Server');
    const [changeToGroup, setChangeToGroup] = useState(null);
    const [displayDirtyPrompt, setDisplayDirtyPrompt] = useState(false);

    const [infraThresholds, setInfraThresholds] = useState([]);
    const [cbQosClasses, setCbQosClasses] = useState([]);

    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        shouldUnregister: false,
        defaultValues: defaultFormValues,
        mode: 'onChange',
    });
    const {
        reset,
        setError,
        setValue,
        handleSubmit,
        formState: { isDirty, dirtyFields },
    } = methods;

    const visibleInfrastructureThresholds = useMemo(() => {
        const filtered = infraThresholds.filter((item) => item.group === infraGroup);
        if (infraGroup === INFRASTRUCTURE_CBQOS) {
            // portal/ui sorts CBQoS entries alphabetically by class name (cbQosKeyBy)
            return _.orderBy(
                filtered,
                [
                    (threshold) => {
                        const metricId = getCbQosMetricId(threshold.metricIds, cbQosClasses);
                        return getCbQosTitle(cbQosClasses, metricId).toLowerCase();
                    },
                ],
                ['asc']
            );
        }
        return filtered;
    }, [infraThresholds, infraGroup, cbQosClasses]);

    const { initialized, finding, resetForm, formData } = useFind({
        queryFunc: async () => {
            const [thresholdsResponse, cbQosResponse] = await Promise.all([
                dataService.get(INFRA_THRESHOLD_URL),
                getCbQosClasses(dataService),
            ]);

            let thresholds = normalizeThresholdsResponse(thresholdsResponse);
            const hadMissing = await createMissingInfrastructureThresholds(dataService, thresholds);

            if (hadMissing) {
                const completeResponse = await dataService.get(INFRA_THRESHOLD_URL);
                thresholds = normalizeThresholdsResponse(completeResponse);
            }

            return {
                thresholds,
                cbQosClasses: cbQosResponse || [],
            };
        },
        postFindHandler: (response) => {
            const thresholds = response?.thresholds || [];
            const cbQosClassList = response?.cbQosClasses || [];
            setCbQosClasses(cbQosClassList);
            setInfraThresholds(thresholds);
            // Use the latest group if user switched during save
            const groupToUse = changeToGroup || infraGroup;
            if (changeToGroup) {
                setInfraGroup(changeToGroup);
                setChangeToGroup(null);
            }
            return {
                ...buildInfraFormDefaults(thresholds, cbQosClassList),
                [GROUP_TYPE_FIELD]: groupToUse,
            };
        },
        resetFunc: reset,
        defaultValues: defaultFormValues,
        errorHandler: errorWrapper({}),
    });

    const handleFormSubmit = (dataToSave = {}) => {
        return formSaveService.save({
            url: INFRA_THRESHOLD_URL,
            saveFunc: async () => {
                const thresholdValidationErrors = {};

                const updates = visibleInfrastructureThresholds.map((threshold) => {
                    const value = _.get(dataToSave, ['byId', threshold._id], {});
                    return updateInfrastructureThreshold(dataService, threshold._id, value).catch((err) => {
                        const validationErrors = err?.responseBody?.details?.errors || err?.responseBody?.validations;
                        if (_.isEmpty(validationErrors)) {
                            throw err;
                        }

                        _.forEach(validationErrors, (validationErr, key) => {
                            thresholdValidationErrors[`${threshold._id}.${key}`] = _.isObject(validationErr)
                                ? validationErr
                                : { message: validationErr };
                        });

                        return null;
                    });
                });

                await Promise.all(updates);

                if (!_.isEmpty(thresholdValidationErrors)) {
                    const error = new Error('Threshold validation failed');
                    error.thresholdValidationErrors = thresholdValidationErrors;
                    throw error;
                }

                // Update CBQoS class enable states after threshold save succeeds
                if (infraGroup === INFRASTRUCTURE_CBQOS) {
                    await updateCbQosClasses(dataToSave);
                }

                return resetForm();
            },
            dataToSave,
            setError,
            validationFieldMapper: mapThresholdValidationField,
            saveErrorHandler: handleThresholdSaveError,
        });
    };

    const updateCbQosClasses = async (dataToSave) => {
        const cbQosPromises = [];
        visibleInfrastructureThresholds.forEach((threshold) => {
            const formValue = _.get(dataToSave, ['byId', threshold._id], {});
            const { cbQosOption } = formValue;
            if (!cbQosOption) return;

            const enableMonitoring = cbQosOption !== CBQOS_OPTION_DISABLED;
            const enableStatus = cbQosOption === CBQOS_OPTION_ENABLED_STATUS;
            const metricIds = formValue.cbQosMetricIds || threshold.metricIds;

            metricIds.forEach((metricId) => {
                const cbQosClass = _.find(cbQosClasses, { metricId });
                if (
                    cbQosClass &&
                    (cbQosClass.enableMonitoring !== enableMonitoring || cbQosClass.enableStatus !== enableStatus)
                ) {
                    cbQosPromises.push(
                        updateCbQosClass(dataService, {
                            ...cbQosClass,
                            enableMonitoring,
                            enableStatus,
                        })
                    );
                }
            });
        });
        if (cbQosPromises.length > 0) {
            await Promise.all(cbQosPromises);
        }
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit);
    const isDirtyFn = (isFormDirty) => isFormDirty;
    const hasUnsavedChanges = () => isDirty && !_.isEmpty(_.omit(dirtyFields, [GROUP_TYPE_FIELD]));

    const handleGroupChange = async (event) => {
        const { value: nextGroup } = event.target;
        if (!nextGroup || nextGroup === infraGroup) {
            return;
        }

        if (hasUnsavedChanges()) {
            // NgpSelect updates form value before onChangeFunc fires, so revert while prompting.
            setValue(GROUP_TYPE_FIELD, infraGroup, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
            setChangeToGroup(nextGroup);
            setDisplayDirtyPrompt(true);
            return;
        }

        setInfraGroup(nextGroup);
        setChangeToGroup(null);
        // Reset to DB values for the new group. Timeout needed for NgpSelect to render correctly first.
        setTimeout(() => {
            reset({ ...formData, [GROUP_TYPE_FIELD]: nextGroup });
            // Mark GROUP_TYPE_FIELD dirty so cross-tab navigation triggers the unsaved-changes blocker.
            setTimeout(() => setValue(GROUP_TYPE_FIELD, nextGroup, { shouldDirty: true }), 200);
        }, 200);
    };

    const handleDirtyFormConfirm = async () => {
        setInfraGroup(changeToGroup);
        setChangeToGroup(null);

        reset({ ...formData, [GROUP_TYPE_FIELD]: changeToGroup });
    };

    useImperativeHandle(ref, () => ({
        save: handleSubmit(handleSubmitFunction),
    }));

    if (!initialized || finding || saving) {
        return (
            <Box p={3} display="flex" alignItems="center" justifyContent="center">
                <CircularProgress size={32} />
            </Box>
        );
    }

    return (
        <FormProvider {...methods}>
            <NgpTwoButtonDialog
                title={dirtyFormTitleText}
                confirmButtonText={dirtyFormConfirmText}
                cancelButtonText={dirtyFormCancelText}
                open={displayDirtyPrompt}
                setOpen={setDisplayDirtyPrompt}
                onConfirm={handleDirtyFormConfirm}
                isWarning
                disableBackdropClick
                closeInTitleBar={false}
            >
                <div className={dirtyPromptClasses.formConfirmDiv}>{dirtyFormBodyText}</div>
            </NgpTwoButtonDialog>

            <NgpForm
                submitFunction={handleSubmitFunction}
                initialized={initialized}
                loading={finding || saving}
                isDirtyFunc={isDirtyFn}
                showRequiredFieldsTip={false}
            >
                <FormSection>
                    <RowFlexLayout vBottom mt={3}>
                        <Typography variant="h6">{t`Infrastructure Type:`}</Typography>
                        <NgpSelect
                            fieldName={GROUP_TYPE_FIELD}
                            required
                            onChangeFunc={handleGroupChange}
                            className={classes.group}
                        >
                            {INFRA_GROUPS.map((group) => (
                                <MenuItem key={group} value={group}>
                                    {group}
                                </MenuItem>
                            ))}
                        </NgpSelect>
                    </RowFlexLayout>
                </FormSection>
                {infraGroup !== INFRASTRUCTURE_CBQOS &&
                    visibleInfrastructureThresholds.map((threshold) => {
                        const thresholdId = threshold._id;
                        const metricLabel = metricLabelForInfrastructure(infraGroup, threshold);
                        const unitSuffix = threshold.units ? ` (${threshold.units})` : '';
                        const thresholdFormData = _.get(methods.getValues(), ['byId', thresholdId], {});
                        const showDegraded = _.get(thresholdFormData, 'enableDegraded', true);
                        const showExcessive = _.get(thresholdFormData, 'enableExcessive', true);
                        const inputInteger = _.get(thresholdFormData, 'inputInteger', false);

                        return (
                            <FormSection key={thresholdId}>
                                <Typography variant="h6">{`${metricLabel}${unitSuffix}`}</Typography>
                                <RowFlexLayout vBottom ml={7}>
                                    <Typography variant="subtitle2">{t`Global Defaults`}</Typography>
                                    {showDegraded && (
                                        <Box ml={4}>
                                            <NgpNativeNumberField
                                                required
                                                fieldName={`byId.${thresholdId}.degraded`}
                                                label={t`Degraded`}
                                                min={threshold.min}
                                                max={threshold.max}
                                                step={inputInteger ? 1 : 'any'}
                                                style={{ marginLeft: 16 }}
                                            />
                                        </Box>
                                    )}
                                    {showExcessive && (
                                        <Box ml={4}>
                                            <NgpNativeNumberField
                                                required
                                                fieldName={`byId.${thresholdId}.critical`}
                                                label={t`Excessive`}
                                                min={threshold.min}
                                                max={threshold.max}
                                                step={inputInteger ? 1 : 'any'}
                                                style={{ marginLeft: 16 }}
                                            />
                                        </Box>
                                    )}
                                </RowFlexLayout>
                            </FormSection>
                        );
                    })}

                {infraGroup === INFRASTRUCTURE_CBQOS && (
                    <div>
                        <Typography className={classes.cbqosHeader}>
                            {visibleInfrastructureThresholds.length > 0 ? (
                                <>
                                    {t`CBQoS Discards`}
                                    <span className={classes.thresholdUnits}>{t` (count per polling interval)`}</span>
                                </>
                            ) : (
                                <span className={classes.thresholdUnits}>{t`No CBQoS classes found`}</span>
                            )}
                        </Typography>

                        {visibleInfrastructureThresholds.map((threshold) => {
                            const metricId = getCbQosMetricId(threshold.metricIds, cbQosClasses);
                            const metricLabel = getCbQosTitle(cbQosClasses, metricId);
                            return (
                                <MemoizedCbQosThresholdRow
                                    key={threshold._id}
                                    threshold={threshold}
                                    metricLabel={metricLabel}
                                    classes={classes}
                                />
                            );
                        })}
                    </div>
                )}
            </NgpForm>
        </FormProvider>
    );
});

export default ThresholdsInfrastructureTab;
